import type { MultiLanguageCompositionSetup } from "@lib/messageComposition";
import type { DndDisplayType, CommonColorProfileProps, HtmlPageMap } from "@lib/messageComposition/inAppMessage";

import type { StoredGrapesData } from "@grapesjs/core/types";

export const ColorProfileType = 0;
export const CssTemplateType = 1;
export const HtmlDndMessageTemplateType = 2;
export const HtmlCustomCodeMessageTemplateType = 3;

export type IamTemplateType =
  | typeof ColorProfileType
  | typeof CssTemplateType
  | typeof HtmlDndMessageTemplateType
  | typeof HtmlCustomCodeMessageTemplateType;

export interface ColorProfileButton {
  backgroundColor: number;
  textColor: number;
  borderColor?: number;
}

// Common fields in the front-end template objects
export interface IamTemplateCommonFields {
  /**
   * If true, values from this template will be used to populate fields when creating a new template
   */
  id: string;
  name: string;
  description: string;
  type: IamTemplateType;
  tagNames: string[];
  team: string;
  isArchived: boolean;
  lastEditedBy?: ChangedByInfo;
  createdByInfo?: ChangedByInfo;
  createdBy: string;
  lastEditedAt?: number;
  isDefault: boolean;
}

interface ChangedByInfo {
  time: number;
  name: string;
  id: string;
}

// camelCased Css Template the front-end prefers
export interface CssTemplate extends IamTemplateCommonFields {
  type: typeof CssTemplateType;
  css: string;
}

// camelCased Color Profile the front-end prefers
export interface ColorProfile extends IamTemplateCommonFields {
  type: typeof ColorProfileType;
  buttons: ColorProfileButton[];
  closeButtonColor: number;
  backgroundColor: number;
  bodyTextColor: number;
  headerTextColor: number;
  frameColor: number;
  iconColor: number;
  iconBackgroundColor: number;
}

export interface HtmlDndMessageTemplate extends Omit<IamTemplateCommonFields, "isDefault"> {
  type: typeof HtmlDndMessageTemplateType;
  grapesJsData: StoredGrapesData;
  dndDisplayType: DndDisplayType;
  html: HtmlPageMap;
  messageVariationId?: string;
  messageCampaignId?: string;
  multiLanguageCompositionSetup: MultiLanguageCompositionSetup | null;
}

// camelCase object that the front-end wants to work with
export type IamTemplate = CssTemplate | ColorProfile | HtmlDndMessageTemplate;

// Common fields retrieved from the API for IAM templates
interface GetIamTemplateResultCommonFields {
  id: string;
  color_profile_id: string;
  color_profile_name: string;
  color_profile_description: string;
  profile_type: IamTemplateType;
  tag_names: string[];
  territory_ids: string[];
  marked_as_deleted: boolean;
  created_by: string;
  last_edited_by: string;
  last_edited_info?: ChangedByInfoResultCommonFields;
  created_by_info?: ChangedByInfoResultCommonFields;
  last_edited_at?: number;
  default: boolean;
}

interface ChangedByInfoResultCommonFields {
  time: number;
  name: string;
  d_id: string;
}

// API response object for Color Profiles, snake_cased
export interface GetColorProfileResult extends GetIamTemplateResultCommonFields {
  profile_type: typeof ColorProfileType;
  buttons: {
    background_color: number;
    text_color: number;
    border_color?: number;
  }[];
  close_button_color: number;
  background_color: number;
  body_text_color: number;
  header_text_color: number;
  frame_color: number;
  icon_color: number;
  icon_background_color: number;
}

// API response object for Css Template
export interface GetCssTemplateResult extends GetIamTemplateResultCommonFields {
  profile_type: typeof CssTemplateType;
  custom_css: string;
  close_button_color: null;
  background_color: null;
  body_text_color: null;
  header_text_color: null;
  frame_color: null;
  icon_color: null;
  icon_background_color: null;
  buttons: [
    {
      background_color: null;
      text_color: null;
      border_color: null;
    },
    {
      background_color: null;
      text_color: null;
      border_color: null;
    },
  ];
}

// API response object for DnD Template
export interface GetHtmlDndMessageTemplateResult extends Omit<GetIamTemplateResultCommonFields, "default"> {
  profile_type: typeof HtmlDndMessageTemplateType;
  grapes_js_data: StoredGrapesData;
  dnd_display_type: DndDisplayType;
  html: HtmlPageMap;
  message_variation_id?: string;
  message_campaign_id?: string;
  multi_language_composition_setup: MultiLanguageCompositionSetup | null;
}

// snake_cased object that the API returns
export type GetIamTemplateResult = GetColorProfileResult | GetCssTemplateResult | GetHtmlDndMessageTemplateResult;

// Response from API when a single object is returned
export interface PostIamTemplateResult {
  info: string;
  template: GetIamTemplateResult;
}

// Response from API when multiple objects are returned
export interface MultipleIamTemplateResults {
  hits: number;
  results: GetIamTemplateResult[];
}

// Either a single IAM Template or an array of them
export type GetIamTemplateResultOrResults = GetIamTemplateResult | MultipleIamTemplateResults;

// Common properties expected by the API when upserting
interface PostIamTemplateCommonProps {
  // id === null means you're creating a new object, otherwise you're updating
  id?: string;
  name: string;
  marked_as_deleted: boolean;
  description: string;
  profile_type: IamTemplateType;
  tag_names: string[];
  territory_ids: string[];
  default: boolean;
}

// Request object to upsert a Color Profile to the IAM Templates
export interface PostColorProfileProps extends CommonColorProfileProps, PostIamTemplateCommonProps {
  profile_type: typeof ColorProfileType;
  button1_background_color: number;
  button1_text_color: number;
  button1_border_color: number;
  button2_background_color: number;
  button2_text_color: number;
  button2_border_color: number;
}

// Request object to upsert a CSS Template to the IAM Templates
export interface PostCssTemplateProps extends PostIamTemplateCommonProps {
  profile_type: typeof CssTemplateType;
  custom_css: string;
}

// Request object to upsert a DnD Template to the IAM Templates
export interface PostDndTemplateProps extends Omit<PostIamTemplateCommonProps, "default"> {
  profile_type: typeof HtmlDndMessageTemplateType;
  grapes_js_data: StoredGrapesData;
  dnd_display_type: string;
  html: HtmlPageMap;
  message_variation_id?: string;
  message_campaign_id?: string;
  multi_language_composition_setup: MultiLanguageCompositionSetup | null;
}

// Request object to upsert an IAM template
export type PostIamTemplateProps = PostColorProfileProps | PostCssTemplateProps | PostDndTemplateProps;

export interface EngagementIamTemplateAdditionalParams {
  message_variation_id?: string;
  omit_archived?: boolean;
  profile_type?: typeof HtmlDndMessageTemplateType;
}
